// authService.ts
import {
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
  Log
} from "oidc-client";
import mitt from "mitt"; 
export const authEvents = mitt();

Log.logger = console;
Log.level = Log.INFO;

const settings: UserManagerSettings = {
  authority: process.env.VUE_APP_authority,
  client_id: process.env.VUE_APP_clientId,

  // --- Redirect Endpoints (Must match your routing) ---
  redirect_uri: window.location.origin + "/auth/signinwin/main",
  popup_redirect_uri: window.location.origin + "/auth/signinpop/main",
  popup_post_logout_redirect_uri: window.location.origin + "/login",

  // Silent renew iframe
  silent_redirect_uri: window.location.origin + "/auth/signinsilent/main",

  post_logout_redirect_uri: window.location.origin + "/login",

  // --- Auth Basics ---
  response_type: "id_token token",
  scope: "openid profile email tdsapi",

  // --- Storage ---
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // --- Silent Renew ---
  automaticSilentRenew: true,
  monitorSession: true,       // helps with OpenIddict session change tracking

  // --- CORS / Cookies ---
  loadUserInfo: false
};

export class AuthService {
  private manager: UserManager;

  constructor() {
    this.manager = new UserManager(settings);

    // 🟢 Optional: auto-log errors
    this.manager.events.addSilentRenewError(() => null);

    // 🔵 When silent renew produces a valid user → notify UI
    this.manager.events.addUserLoaded((user) => {
      if (user && !user.expired) {
        authEvents.emit("silentSSO-success", user);
      }
    });

    this.manager.events.addSilentRenewError(() => {
      // Silent failed → go to normal login
      authEvents.emit("silentSSO-failed");
    });

    // 🔵 When token expired
    this.manager.events.addAccessTokenExpired(() => {
      // Try silent renew first
      this.manager.signinSilent().catch(() => {
        authEvents.emit("session-expired");
      });
    });
  }

  // 🔵 1. NORMAL LOGIN WINDOW
  async signinRedirect() {
    return this.manager.signinRedirect();
  }

  // 🟡 2. NORMAL CALLBACK
  async handleSigninRedirectCallback() {
    return this.manager.signinRedirectCallback();
  }

  // 🟢 3. SILENT SSO (Iframe)
  async signinSilent() {
    try {
      const user = await this.manager.signinSilent();
      return user;
    } catch {
      return null;
    }
  }

  // 🔵 4. SILENT CALLBACK HANDLER (signinsilent page)
  async handleSilentCallback() {
    try {
      await this.manager.signinSilentCallback();
    } catch {
      return null;
    }
  }

  // 🔵 5. GET USER
  getUser() {
    return this.manager.getUser();
  }

  // 🔵 6. LOGOUT
  signout() {
    return this.manager.signoutRedirect();
  }
}

export const authService = new AuthService();